import {
  GridCellKind,
  type CustomCell,
  type CustomRenderer,
  type ProvideEditorCallback,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ImageArrayValue } from '@makyoapp/makyo-common'

import { IImageArrayCell } from '@/core/models/columns/imageArray'
import { STORAGE_KEYS } from '@/shared/constants'
import { ImageManager } from '@/components/ImageManager/ImageManager'

import { drawImage, itemMargin } from '../helpers/draw-image'
import { getEditorStyle } from '../helpers/editor-style-helper'

/**
 * Interface defining the props for a custom image array cell.
 */
export interface CustomImageArrayCellProps {
  readonly type: ColumnTypes
  readonly cell: IImageArrayCell
}

export type CustomImageArrayCell = CustomCell<CustomImageArrayCellProps>

// Constants for configuration
const MAX_VISIBLE_IMAGES = 4
const OVERLAY_MARGIN = itemMargin + 3
const EDITOR_WIDTH = 600

/**
 * Editor component for the custom image array cell.
 * Renders an ImageManager for editing the cell's image array.
 */
const Editor: ReturnType<ProvideEditorCallback<CustomImageArrayCell>> = ({ value, onFinishedEditing }) => {
  const { cell } = value.data
  const cellValues = cell.value

  const handleChange = (_ty: any, newImageArrayValue: any) => {
    const imageArrayValueString = JSON.stringify(newImageArrayValue)
    onFinishedEditing({
      ...value,
      data: {
        ...value.data,
        cell: cell.toCell(imageArrayValueString) as IImageArrayCell,
      },
    })
  }

  return (
    <div className='bg-white border bdr'>
      <ImageManager
        modal={false}
        id='mk-gg-img-editor'
        show
        multiple
        onHide={() => {}}
        initialSelected={cellValues as any}
        onChange={handleChange}
      />
    </div>
  )
}

/**
 * Draws a placeholder icon when there are no images in the cell.
 */
const drawPlaceholder = (ctx: CanvasRenderingContext2D, rect: { x: number; y: number; height: number }, theme: any) => {
  ctx.fillStyle = theme.textDark
  ctx.font = '20px Material Symbols Rounded'
  ctx.fillText('add_box', rect.x + theme.cellHorizontalPadding, rect.y + rect.height / 2)
}

/**
 * Draws the images in the cell, up to the maximum visible count.
 * Returns the size of each image drawn.
 */
const drawImages = (args: any, rect: any, urls: string[]) => {
  const imageSize = Math.min((rect.width - 3 * itemMargin) / MAX_VISIBLE_IMAGES, rect.height)

  urls.slice(0, MAX_VISIBLE_IMAGES).forEach((url, index) => {
    const x = rect.x + index * (imageSize + itemMargin)
    const y = rect.y
    const imageRect = { ...rect, x, y, width: imageSize, height: imageSize }
    drawImage({ ...args, rect: imageRect }, url, MAX_VISIBLE_IMAGES, 'left')
  })

  return imageSize
}

/**
 * Draws an overlay on the last visible image when there are more images than can be displayed.
 * Shows a count of the remaining images.
 */
const drawOverlay = (
  ctx: CanvasRenderingContext2D,
  lastImageX: number,
  lastImageY: number,
  imageSize: number,
  remainingCount: number
) => {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.fillRect(lastImageX + OVERLAY_MARGIN, lastImageY, imageSize, imageSize)

  ctx.font = 'bold 16px sans-serif'
  ctx.fillStyle = 'white'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(`+${remainingCount}`, lastImageX + imageSize / 2 + OVERLAY_MARGIN, lastImageY + imageSize / 2)
}

/**
 * Custom renderer for the image array cell.
 * Handles drawing the cell content and providing the editor component.
 */
const CustomImageArrayRenderer: CustomRenderer<CustomImageArrayCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomImageArrayCell =>
    (cell.data as CustomImageArrayCellProps).type === ColumnTypes.ImageArray,

  /**
   * Draws the content of the cell, including images and overlay if necessary.
   */
  draw: (args, cell) => {
    const { ctx, rect, theme } = args
    const cellValues = cell.data.cell.value as unknown as ImageArrayValue[]

    if (!Array.isArray(cellValues) || cellValues.length === 0) {
      drawPlaceholder(ctx, rect, theme)
      return
    }

    const urls = cellValues.map((cellValue) => cellValue.Url)
    const imageSize = drawImages(args, rect, urls)

    if (urls.length > MAX_VISIBLE_IMAGES) {
      ctx.save()
      const lastImageX = rect.x + 3 * (imageSize + itemMargin)
      const lastImageY = rect.y
      drawOverlay(ctx, lastImageX, lastImageY, imageSize, urls.length - MAX_VISIBLE_IMAGES)
      ctx.restore()
    }

    return true
  },

  /**
   * Provides the editor component and its styling for the cell.
   * Adjusts the editor's appearance based on the current image manager tab.
   */
  provideEditor: () => {
    const imageManagerTab = localStorage.getItem(STORAGE_KEYS.IMG_MGR_TAB)

    const style = {
      maxWidth: `${EDITOR_WIDTH}px`,
      width: `${EDITOR_WIDTH}px`,
      overflow: 'hidden',
      boxShadow: 'unset',
      backgroundColor: 'transparent',
    }

    const editorHeights: { [key: string]: number } = {
      stockPhotos: 270,
      imageUrl: 110,
      myImages: 607,
    }

    const editorHeight = editorHeights[imageManagerTab || ''] || 0
    const customEditorHeight = getEditorStyle(editorHeight, true)

    return {
      disablePadding: true,
      disableStyling: false,
      styleOverride: { ...style, ...customEditorHeight },
      editor: Editor,
    }
  },
}

export default CustomImageArrayRenderer
